import React, { Fragment } from 'react';
import { Modal, ButtonToolbar } from 'reactstrap';
import Button from '@material-ui/core/Button';
import moment from 'moment';
import GavelIcon from 'mdi-react/GavelIcon';
import OpenInNewIcon from 'mdi-react/OpenInNewIcon';
import FileDocumentOutlineIcon from 'mdi-react/FileDocumentOutlineIcon';
import ChevronRightIcon from 'mdi-react/ChevronRightIcon';
import { _capitalizeText, _replaceAbbreviations, _formatNumber } from '../../../shared/helpers/utils';

const _formatStageLabel = (stage) => {
  if (!stage) return '';
  return stage
    .toString()
    .split('_')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const _formatDate = (val) => {
  if (!val) return '--';
  const m = moment(val);
  if (!m.isValid() || m.year() < 1971) return '--';
  return m.format('MMMM D, YYYY');
};

const _parseDocs = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

const labelStyle = { fontSize: 11, color: '#858AA1', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4, fontWeight: 600 };
const valueStyle = { fontSize: 13, color: '#23282C', fontWeight: 500, wordBreak: 'break-word' };
const sectionStyle = { padding: '16px 0', borderTop: '1px solid #EEF0F5' };

const LisPendensModal = ({ isOpen, onClose, foreclosureCase, property }) => {
  if (!foreclosureCase) return null;

  const fc = foreclosureCase;
  const docs = _parseDocs(fc.case_documents);
  const stages = Array.isArray(fc.case_stages) ? fc.case_stages : (fc.case_stages ? [fc.case_stages] : []);
  const propertyAddress = property
    ? `${_capitalizeText(_replaceAbbreviations(property.display_full_street_address || property.full_street_address || ''))}${property.address_2_normalized ? ', ' + property.address_2_normalized.toUpperCase().replace('APT', 'Apt') : ''}`
    : '';
  const cityLine = property
    ? `${property.neighborhood && property.neighborhood[0] ? _capitalizeText(property.neighborhood[0]) + ', ' : ''}${_capitalizeText(property.borough || '')} ${property.zip || ''}`
    : '';

  return (
    <Modal
      isOpen={isOpen}
      modalClassName="ltr-support"
      className="modal-dialog--primary modal-dialog--header"
      toggle={onClose}
      style={{ minWidth: 720 }}
      scrollable
    >
      <div className="modal__header">
        <button className="lnr lnr-cross modal__close-btn" onClick={onClose} />
        <h4 className="modal__title">
          <GavelIcon size={22} style={{ marginRight: 8, marginTop: -2 }} />
          Lis Pendens{fc.index_number ? ' — ' + fc.index_number : ''}
        </h4>
      </div>

      <div className="modal__body" style={{ textAlign: 'left' }}>
        {propertyAddress && (
          <div style={{ paddingBottom: 16 }}>
            <div style={labelStyle}>Property</div>
            <div style={{ ...valueStyle, fontSize: 15 }}>{propertyAddress}</div>
            {cityLine.trim() && <div style={{ fontSize: 12, color: '#858AA1', marginTop: 2 }}>{cityLine}</div>}
          </div>
        )}

        <div style={sectionStyle}>
          <div style={labelStyle}>Caption</div>
          <div style={valueStyle}>{fc.caption ? _capitalizeText(fc.caption) : '--'}</div>
        </div>

        <div style={{ ...sectionStyle, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <div style={labelStyle}>Creditor / Plaintiff</div>
            <div style={valueStyle}>{fc.creditor ? _capitalizeText(fc.creditor) : '--'}</div>
          </div>
          <div>
            <div style={labelStyle}>Debtor / Defendant</div>
            <div style={valueStyle}>{fc.debtor ? _capitalizeText(fc.debtor) : '--'}</div>
          </div>
        </div>

        <div style={{ ...sectionStyle, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
          <div>
            <div style={labelStyle}>Original Amount</div>
            <div style={valueStyle}>{fc.original_amount ? '$' + _formatNumber(fc.original_amount) : '--'}</div>
          </div>
          <div>
            <div style={labelStyle}>Court</div>
            <div style={valueStyle}>{fc.court ? _capitalizeText(fc.court) : '--'}</div>
          </div>
          <div>
            <div style={labelStyle}>Case Type</div>
            <div style={valueStyle}>{fc.case_type ? _capitalizeText(fc.case_type) : '--'}</div>
          </div>
        </div>

        <div style={{ ...sectionStyle, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <div style={labelStyle}>Filing Date</div>
            <div style={valueStyle}>{_formatDate(fc.filing_date)}</div>
          </div>
          <div>
            <div style={labelStyle}>Lis Pendens Date</div>
            <div style={valueStyle}>{_formatDate(fc.lis_pendens_date)}</div>
          </div>
        </div>

        {stages.length > 0 && (
          <div style={sectionStyle}>
            <div style={labelStyle}>Case Stage Progression</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4, marginTop: 8 }}>
              {stages.map((stage, idx) => {
                const isCurrent = fc.case_stage === stage;
                const isLast = idx === stages.length - 1;
                return (
                  <Fragment key={idx}>
                    <span
                      style={{
                        padding: '4px 12px',
                        borderRadius: 15,
                        fontSize: 11,
                        fontWeight: 600,
                        background: isCurrent ? '#780F9E' : 'rgba(120,15,158,0.10)',
                        color: isCurrent ? '#FFF' : '#780F9E',
                        border: '1px solid rgba(120,15,158,0.40)',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {_formatStageLabel(stage)}
                    </span>
                    {!isLast && (
                      <ChevronRightIcon size={16} color="#A3ADC2" style={{ flexShrink: 0 }} />
                    )}
                  </Fragment>
                );
              })}
            </div>
          </div>
        )}

        {docs.length > 0 && (
          <div style={sectionStyle}>
            <div style={labelStyle}>Case Documents ({docs.length})</div>
            <div style={{ marginTop: 8, maxHeight: 220, overflowY: 'auto', border: '1px solid #EEF0F5', borderRadius: 6 }}>
              {docs.map((doc, idx) => (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '10px 14px',
                    borderBottom: idx < docs.length - 1 ? '1px solid #EEF0F5' : 'none',
                    fontSize: 12
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <FileDocumentOutlineIcon size={16} color={'#858AA1'} />
                    <div>
                      <div style={{ fontWeight: 600, color: '#23282C' }}>
                        Doc #{doc.doc_number}{doc.title ? ' — ' + _capitalizeText(doc.title) : ''}
                      </div>
                      {doc.filed_date && (
                        <div style={{ color: '#858AA1', fontSize: 11, marginTop: 2 }}>Filed {doc.filed_date}</div>
                      )}
                    </div>
                  </div>
                  {doc.nyscef_url && (
                    <a
                      href={doc.nyscef_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#780F9E', display: 'inline-flex', alignItems: 'center', gap: 4, fontWeight: 500 }}
                    >
                      View <OpenInNewIcon size={14} />
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {fc.last_checked_at && (
          <div style={{ paddingTop: 12, fontSize: 11, color: '#858AA1' }}>
            Source: NYSCEF • Last checked {moment(fc.last_checked_at).fromNow()}
          </div>
        )}
      </div>

      <ButtonToolbar className="modal__footer offering-status">
        <hr />
        {fc.case_url && (
          <Button
            className="btn btn-outline-primary"
            color="primary"
            variant="outlined"
            onClick={() => window.open(fc.case_url, '_blank')}
            style={{ marginRight: 8 }}
          >
            Open in NYSCEF <OpenInNewIcon size={14} style={{ marginLeft: 4 }} />
          </Button>
        )}
        <Button
          className="modal_ok btn btn-primary"
          onClick={onClose}
          color="primary"
        >
          Close
        </Button>
      </ButtonToolbar>
    </Modal>
  );
};

export default LisPendensModal;
